import React, { useState, useEffect } from 'react';
import { MagicParticles, FlyingBat, NightSky, CastleSilhouette } from './shared/Effects';
import { getCourses, enrollInCourse } from '../api';
import CourseSlideshow from './CourseSlideshow';

// Default courses
const defaultCourses = [
  { id: 1, emoji: '🐍', title: 'Python Magic', slug: 'python-magic', age_range: '8-11 ani', age_min: 8, age_max: 11, color: '#50fa7b', short_description: 'Învață Python creând jocuri și animații! Transformă-te în vrăjitorul codului.', description: 'Cursul Python Magic introduce copiii în lumea programării prin proiecte distractive. Fiecare lecție construiește un joc sau o animație nouă, de la șerpi pixelați la aventuri text-based. Copiii învață variabile, bucle, condiții și funcții într-un mod interactiv și captivant.', total_lessons: 12, level_display: 'Începător' },
  { id: 2, emoji: '🌐', title: 'HTML Castelul', slug: 'html-castelul', age_range: '7-10 ani', age_min: 7, age_max: 10, color: '#ff79c6', short_description: 'Construiește-ți propriul castel digital cu HTML! Pagini web magice din prima lecție.', description: 'HTML Castelul învață copiii să construiască pagini web de la zero. Fiecare lecție adaugă o cameră nouă castelului digital — de la structura de bază la imagini, link-uri și formulare. La final, fiecare copil are propriul site web publicat!', total_lessons: 10, level_display: 'Începător' },
  { id: 3, emoji: '🎨', title: 'CSS Poțiuni', slug: 'css-potiuni', age_range: '8-11 ani', age_min: 8, age_max: 11, color: '#bd93f9', short_description: 'Colorează web-ul cu poțiunile CSS! Animații, culori și efecte spectaculoase.', description: 'CSS Poțiuni transformă paginile web în opere de artă. Copiii învață culori, fonturi, layout-uri, animații și efecte vizuale. Fiecare "poțiune" CSS adaugă magie vizuală proiectelor lor. Include proiecte de dark mode și responsive design!', total_lessons: 10, level_display: 'Începător' },
  { id: 4, emoji: '🎮', title: 'Game Dev', slug: 'game-dev', age_range: '9-11 ani', age_min: 9, age_max: 11, color: '#ffb86c', short_description: 'Creează jocuri ca un adevărat game developer! De la idee la joc funcțional.', description: 'Game Dev este cursul suprem pentru micii gameri. Începem cu Scratch pentru concepte de bază, apoi trecem la JavaScript pentru jocuri în browser. Copiii creează platformere, puzzle-uri și jocuri de aventură, învățând logica din spatele jocurilor preferate.', total_lessons: 14, level_display: 'Intermediar' },
];

const FORMATS = [
  { id: '1to1', icon: '🧑‍🏫', title: 'Cursuri 1-to-1', subtitle: 'Private', desc: 'Atenție 100% pe copilul tău. Lecții personalizate, ritm propriu.', price: '80', ideal: 'Începători sau progres rapid', color: '#ff79c6' },
  { id: 'small', icon: '👥', title: 'Clase Mici', subtitle: '3-5 copii', desc: 'Învață cu prietenii! Grupuri mici, interacțiune maximă, proiecte colaborative.', price: '50', ideal: 'Copii sociabili, lucru în echipă', color: '#bd93f9' },
  { id: 'large', icon: '🏫', title: 'Clase Mari', subtitle: '8-12 copii', desc: 'Energie de clasă! Workshop-uri, competiții și spirit de echipă.', price: '30', ideal: 'Experiența de grup, preț accesibil', color: '#50fa7b' },
];
/* ═══════════════════════════════════════
   COURSE DETAIL MODAL
   ═══════════════════════════════════════ */
const CourseDetail = ({ course, onClose, onEnroll }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 200,
    background: 'rgba(10,10,25,0.95)', backdropFilter: 'blur(16px)',
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    padding: 24, overflowY: 'auto',
  }}>
    <div style={{
      background: '#16181e', border: `2px solid ${course.color}30`,
      borderRadius: 20, padding: '40px 32px', maxWidth: 520, width: '100%',
      position: 'relative',
    }}>
      <button onClick={onClose} style={{
        position: 'absolute', top: 16, right: 16,
        background: '#ff555520', border: '1px solid #ff555530', borderRadius: 8,
        width: 32, height: 32, color: '#ff5555', fontSize: 14,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>✕</button>

      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: 64, marginBottom: 12 }}>{course.emoji}</div>
        <h2 style={{ fontSize: 26, fontWeight: 900, color: course.color, marginBottom: 4, fontFamily: "'Nunito', sans-serif" }}>{course.title}</h2>
        <div style={{ display: 'inline-block', background: `${course.color}20`, border: `1px solid ${course.color}30`, borderRadius: 20, padding: '4px 16px', fontSize: 13, color: course.color, fontWeight: 700 }}>{course.age_range}</div>
      </div>

      <p style={{ color: '#b0b4c8', fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
        {course.description || course.short_description}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
        <div style={{ background: '#13141a', borderRadius: 12, padding: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: 24, marginBottom: 4 }}>📚</div>
          <div style={{ color: '#f8f8f2', fontSize: 18, fontWeight: 800 }}>{course.total_lessons}</div>
          <div style={{ color: '#6272a4', fontSize: 11 }}>lecții</div>
        </div>
        <div style={{ background: '#13141a', borderRadius: 12, padding: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: 24, marginBottom: 4 }}>🎯</div>
          <div style={{ color: '#f8f8f2', fontSize: 14, fontWeight: 700 }}>{course.level_display}</div>
          <div style={{ color: '#6272a4', fontSize: 11 }}>nivel</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={() => onEnroll(course)} style={{
          flex: 1, background: `linear-gradient(135deg, ${course.color}, ${course.color}cc)`,
          border: 'none', borderRadius: 12, padding: '14px 0',
          color: '#0d0e12', fontWeight: 800, fontSize: 15, cursor: 'pointer',
          fontFamily: "'Nunito', sans-serif",
        }}>
          🚀 Înscrie-te acum
        </button>
        <button onClick={() => onEnroll(course, true)} style={{
          flex: 1, background: 'transparent', border: `2px solid ${course.color}50`,
          borderRadius: 12, padding: '14px 0',
          color: course.color, fontWeight: 700, fontSize: 14, cursor: 'pointer',
          fontFamily: "'Nunito', sans-serif",
        }}>
          🎁 Lecție demo gratuită
        </button>
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════
   ENROLLMENT FORM MODAL
   ═══════════════════════════════════════ */
const EnrollmentForm = ({ course, isDemo, onClose }) => {
  const [form, setForm] = useState({
    childName: '', childAge: '', parentName: '', parentEmail: '',
    parentPhone: '', format: 'small', message: '',
  });
  const [status, setStatus] = useState('form');
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = async () => {
    if (!form.childName || !form.parentEmail || !form.parentPhone) {
      setStatus('error');
      return;
    }
    setStatus('sending');
    try {
      await enrollInCourse({
        courseId: course?.id || 1,
        childName: form.childName,
        childAge: parseInt(form.childAge) || 8,
        parentName: form.parentName,
        parentEmail: form.parentEmail,
        parentPhone: form.parentPhone,
        message: `${isDemo ? '[LECȚIE DEMO GRATUITĂ] ' : ''}Format: ${form.format}. ${form.message}`,
      });
      setStatus('success');
    } catch (e) {
      setStatus('error');
    }
  };

  const inputStyle = (field) => ({
    width: '100%', padding: '12px 14px',
    background: '#0d0e12', border: `1px solid ${focusedField === field ? '#bd93f9' : '#1e2030'}`,
    borderRadius: 10, color: '#f8f8f2', fontSize: 13,
    fontFamily: "'Nunito', sans-serif", outline: 'none', boxSizing: 'border-box',
    transition: 'all 0.2s',
    boxShadow: focusedField === field ? '0 0 0 3px rgba(189,147,249,0.1)' : 'none',
  });

  const labelStyle = { display: 'block', color: '#8be9fd', fontSize: 10, marginBottom: 5, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' };

  if (status === 'success') {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(10,10,25,0.95)', backdropFilter: 'blur(16px)',
        display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 24,
      }}>
        <div style={{ background: '#16181e', borderRadius: 20, padding: '48px 32px', maxWidth: 440, width: '100%', textAlign: 'center', border: '1px solid #50fa7b30' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
          <h2 style={{ color: '#50fa7b', fontSize: 24, fontWeight: 900, marginBottom: 8, fontFamily: "'Nunito', sans-serif" }}>
            {isDemo ? 'Lecție demo programată!' : 'Înscriere trimisă!'}
          </h2>
          <p style={{ color: '#8b90a8', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
            Te vom contacta în curând pe email sau telefon pentru a confirma {isDemo ? 'lecția demo gratuită' : 'înscrierea'}.
          </p>
          <button onClick={onClose} style={{
            background: 'linear-gradient(135deg, #50fa7b, #8be9fd)',
            border: 'none', borderRadius: 12, padding: '12px 32px',
            color: '#0d0e12', fontWeight: 800, fontSize: 14, cursor: 'pointer',
            fontFamily: "'Nunito', sans-serif",
          }}>Super! 🧛</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(10,10,25,0.95)', backdropFilter: 'blur(16px)',
      display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
      padding: 24, overflowY: 'auto',
    }}>
      <div style={{
        background: '#16181e', borderRadius: 20, padding: '32px 28px',
        maxWidth: 500, width: '100%', margin: '40px 0',
        border: '1px solid #1e2030',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 28 }}>{course?.emoji || '🧛'}</span>
              <h2 style={{ fontSize: 20, fontWeight: 900, color: '#f8f8f2', margin: 0, fontFamily: "'Nunito', sans-serif" }}>
                {isDemo ? '🎁 Lecție Demo Gratuită' : '🚀 Înscriere Curs'}
              </h2>
            </div>
            {course && <div style={{ color: course.color || '#bd93f9', fontSize: 13, fontWeight: 700 }}>{course.title} • {course.age_range}</div>}
          </div>
          <button onClick={onClose} style={{
            background: '#ff555510', border: '1px solid #ff555520', borderRadius: 8,
            width: 32, height: 32, color: '#ff5555', fontSize: 14, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>✕</button>
        </div>

        {status === 'error' && (
          <div style={{ background: '#ff555510', border: '1px solid #ff555530', borderRadius: 10, padding: '10px 14px', marginBottom: 16, color: '#ff5555', fontSize: 12 }}>
            ⚠️ Completează câmpurile obligatorii (Nume copil, Email, Telefon).
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Row 1 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 12 }}>
            <div>
              <label style={labelStyle}>Numele copilului *</label>
              <input style={inputStyle('child')} placeholder="Ana" value={form.childName}
                onChange={e => setForm({...form, childName: e.target.value})}
                onFocus={() => setFocusedField('child')} onBlur={() => setFocusedField(null)} />
            </div>
            <div>
              <label style={labelStyle}>Vârsta</label>
              <input style={inputStyle('age')} placeholder="8" type="number" min="7" max="14" value={form.childAge}
                onChange={e => setForm({...form, childAge: e.target.value})}
                onFocus={() => setFocusedField('age')} onBlur={() => setFocusedField(null)} />
            </div>
          </div>

          {/* Row 2 */}
          <div>
            <label style={labelStyle}>Numele părintelui</label>
            <input style={inputStyle('parent')} placeholder="Ion Popescu" value={form.parentName}
              onChange={e => setForm({...form, parentName: e.target.value})}
              onFocus={() => setFocusedField('parent')} onBlur={() => setFocusedField(null)} />
          </div>

          {/* Row 3 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Email *</label>
              <input style={inputStyle('email')} placeholder="email@exemplu.ro" type="email" value={form.parentEmail}
                onChange={e => setForm({...form, parentEmail: e.target.value})}
                onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)} />
            </div>
            <div>
              <label style={labelStyle}>Telefon *</label>
              <input style={inputStyle('phone')} placeholder="07xx xxx xxx" value={form.parentPhone}
                onChange={e => setForm({...form, parentPhone: e.target.value})}
                onFocus={() => setFocusedField('phone')} onBlur={() => setFocusedField(null)} />
            </div>
          </div>

          {/* Format selection */}
          <div>
            <label style={labelStyle}>Alege formatul</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {FORMATS.map(f => (
                <button key={f.id} onClick={() => setForm({...form, format: f.id})} style={{
                  background: form.format === f.id ? `${f.color}15` : '#0d0e12',
                  border: `1px solid ${form.format === f.id ? f.color : '#1e2030'}`,
                  borderRadius: 10, padding: '10px 8px', cursor: 'pointer',
                  textAlign: 'center', transition: 'all 0.2s',
                }}>
                  <div style={{ fontSize: 20, marginBottom: 2 }}>{f.icon}</div>
                  <div style={{ fontSize: 10, color: form.format === f.id ? f.color : '#8b90a8', fontWeight: 700 }}>{f.subtitle}</div>
                  <div style={{ fontSize: 12, color: form.format === f.id ? '#f8f8f2' : '#44475a', fontWeight: 800 }}>{f.price} RON</div>
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <label style={labelStyle}>Mesaj (opțional)</label>
            <textarea style={{...inputStyle('msg'), minHeight: 70, resize: 'vertical'}}
              placeholder="Experiența anterioară, preferințe, întrebări..."
              value={form.message}
              onChange={e => setForm({...form, message: e.target.value})}
              onFocus={() => setFocusedField('msg')} onBlur={() => setFocusedField(null)} />
          </div>

          {/* Submit */}
          <button onClick={handleSubmit} disabled={status === 'sending'} style={{
            background: status === 'sending' ? '#1e2030' : 'linear-gradient(135deg, #ff79c6, #bd93f9)',
            border: 'none', borderRadius: 12, padding: '15px 0',
            color: '#fff', fontWeight: 800, fontSize: 15, cursor: status === 'sending' ? 'wait' : 'pointer',
            fontFamily: "'Nunito', sans-serif", transition: 'all 0.2s',
          }}>
            {status === 'sending' ? '⏳ Se trimite...' : isDemo ? '🎁 Programează lecția demo' : '🚀 Înscrie copilul!'}
          </button>

          <p style={{ textAlign: 'center', color: '#2a2d3a', fontSize: 10 }}>
            {isDemo ? 'Prima lecție e 100% gratuită, fără obligații 🧛' : 'Te contactăm în 24h pentru confirmare'}
          </p>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════
   MAIN KIDS PAGE
   ═══════════════════════════════════════ */
const KidsPage = ({ onSwitchToMain }) => {
  const [visible, setVisible] = useState(false);
  const [courses, setCourses] = useState(defaultCourses);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [enrollCourse, setEnrollCourse] = useState(null);
  const [isDemo, setIsDemo] = useState(false);
  const [hoveredFormat, setHoveredFormat] = useState(null);
  const [showSlideshow, setShowSlideshow] = useState(false);


  // TODO: Schimbă cu link-ul tău Gamma publicat
 <button onClick={() => setShowSlideshow(true)} style={{
  background: 'rgba(68,71,90,0.5)', border: '2px solid #bd93f9',
  borderRadius: 50, padding: '14px 32px',
  color: '#bd93f9', fontFamily: "'Nunito', sans-serif",
  fontWeight: 800, fontSize: 16, cursor: 'pointer',
  transition: 'all 0.2s',
}}
onMouseEnter={e => e.target.style.background = '#bd93f920'}
onMouseLeave={e => e.target.style.background = 'rgba(68,71,90,0.5)'}>
  📺 Vezi un demo
</button>

  useEffect(() => {
    setTimeout(() => setVisible(true), 100);
    getCourses().then(data => {
      if (data && data.length > 0) setCourses(data);
    }).catch(() => {});
  }, []);

  const handleEnroll = (course, demo = false) => {
    setSelectedCourse(null);
    setIsDemo(demo);
    setEnrollCourse(course);
  };

  const openEnrollGeneral = () => {
    setIsDemo(false);
    setEnrollCourse({ emoji: '🧛', title: 'Alege la înscriere', age_range: '7-11 ani' });
  };

  const bgColors = {
    '#50fa7b': 'rgba(80,250,123,0.12)',
    '#ff79c6': 'rgba(255,121,198,0.12)',
    '#bd93f9': 'rgba(189,147,249,0.12)',
    '#ffb86c': 'rgba(255,184,108,0.12)',
  };
  const borderColors = {
    '#50fa7b': 'rgba(80,250,123,0.3)',
    '#ff79c6': 'rgba(255,121,198,0.3)',
    '#bd93f9': 'rgba(189,147,249,0.3)',
    '#ffb86c': 'rgba(255,184,108,0.3)',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 30%, #0f3460 60%, #1a1a2e 100%)',
      color: '#f8f8f2',
      fontFamily: "'Nunito', sans-serif",
      position: 'relative', overflow: 'hidden',
    }}>

      <style>{`
        @keyframes wobble { 0%,100%{transform:rotate(0deg)} 25%{transform:rotate(-5deg)} 75%{transform:rotate(5deg)} }
        @keyframes bounceIn { 0%{transform:scale(0);opacity:0} 60%{transform:scale(1.1)} 100%{transform:scale(1);opacity:1} }
        @keyframes gradientShift { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
        @keyframes fadeInUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      <MagicParticles />
      <NightSky />
      <FlyingBat delay={500} startX={-50} startY={100} size={24} />
      <FlyingBat delay={3000} startX={-80} startY={250} size={18} />

      {/* NAV */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 24px',
        background: 'linear-gradient(180deg, rgba(26,26,46,0.95) 0%, transparent 100%)',
        backdropFilter: 'blur(10px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 28, animation: 'wobble 2s ease-in-out infinite' }}>🧛</span>
          <div>
            <span style={{ fontSize: 20, fontWeight: 900, background: 'linear-gradient(135deg, #ff79c6, #bd93f9, #50fa7b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>DraculaDev</span>
            <div style={{ fontSize: 10, color: '#ffb86c', fontWeight: 700, letterSpacing: 1.5 }}>KIDS ACADEMY</div>
          </div>
        </div>
        <button onClick={onSwitchToMain} style={{
          background: 'rgba(68,71,90,0.6)', border: '1px solid #44475a',
          borderRadius: 20, padding: '8px 20px', color: '#8be9fd',
          fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 13, cursor: 'pointer',
        }}>💻 Servicii Dev</button>
      </nav>

      {/* HERO */}
      <section style={{
        minHeight: '85vh', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center',
        padding: '120px 24px 40px', textAlign: 'center',
        position: 'relative', zIndex: 10,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.95)',
        transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}>
        <div style={{ fontSize: 'clamp(48px, 10vw, 80px)', marginBottom: 8, animation: 'bounceIn 1s cubic-bezier(0.34, 1.56, 0.64, 1)', filter: 'drop-shadow(0 0 20px rgba(189,147,249,0.5))' }}>🏰</div>

        <h1 style={{ fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: 900, lineHeight: 1.2, marginBottom: 16 }}>
          <span style={{ color: '#f8f8f2' }}>Învață să </span>
          <span style={{ background: 'linear-gradient(135deg, #ff79c6, #bd93f9, #50fa7b, #f1fa8c)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'gradientShift 3s ease infinite', backgroundSize: '200% 200%' }}>programezi</span>
          <br /><span style={{ color: '#f8f8f2' }}>ca un </span>
          <span style={{ color: '#ff79c6', textShadow: '0 0 20px rgba(255,121,198,0.5)' }}>vampir digital!</span>
        </h1>

        <p style={{ color: '#8be9fd', fontSize: 'clamp(14px, 2.5vw, 18px)', maxWidth: 500, lineHeight: 1.6, marginBottom: 32, fontWeight: 600 }}>
          Cursuri de programare pentru copii 7-11 ani.<br />Jocuri, animații și aventuri în lumea codului! 🎮
        </p>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button onClick={openEnrollGeneral} style={{
            background: 'linear-gradient(135deg, #ff79c6, #bd93f9)',
            border: 'none', borderRadius: 50, padding: '14px 32px',
            color: '#fff', fontFamily: "'Nunito', sans-serif",
            fontWeight: 800, fontSize: 16, cursor: 'pointer',
            boxShadow: '0 4px 25px rgba(255,121,198,0.4)',
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => e.target.style.transform = 'scale(1.05)'}
          onMouseLeave={e => e.target.style.transform = 'scale(1)'}>
            🚀 Începe aventura!
          </button>
          <button 
  onClick={() => setShowSlideshow(true)} 
  style={{
    background: 'rgba(68,71,90,0.5)', 
    border: '2px solid #bd93f9',
    borderRadius: 50, 
    padding: '14px 32px',
    color: '#bd93f9', 
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 800, 
    fontSize: 16, 
    cursor: 'pointer',
    transition: 'all 0.2s',
  }}
  onMouseEnter={e => e.target.style.background = '#bd93f920'}
  onMouseLeave={e => e.target.style.background = 'rgba(68,71,90,0.5)'}
>
  📺 Vezi un demo
</button>
        </div>
      </section>

      {/* FORMATS — 3 tipuri */}
      <section style={{ padding: '40px 24px 60px', position: 'relative', zIndex: 10, maxWidth: 950, margin: '0 auto' }}>
        <h2 style={{ textAlign: 'center', fontSize: 'clamp(20px, 3.5vw, 28px)', marginBottom: 12, fontWeight: 900 }}>
          🎯 Alege formatul potrivit
        </h2>
        <p style={{ textAlign: 'center', color: '#8b90a8', fontSize: 14, marginBottom: 36, maxWidth: 450, margin: '0 auto 36px' }}>
          Fiecare copil învață diferit. Alege ce funcționează cel mai bine.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18 }}>
          {FORMATS.map((f, i) => (
            <div key={f.id}
              onMouseEnter={() => setHoveredFormat(i)}
              onMouseLeave={() => setHoveredFormat(null)}
              style={{
                background: hoveredFormat === i ? `${f.color}10` : 'rgba(19,20,26,0.8)',
                border: `2px solid ${hoveredFormat === i ? f.color + '50' : 'rgba(255,255,255,0.08)'}`,
                borderRadius: 16, padding: '28px 24px', textAlign: 'center',
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                transform: hoveredFormat === i ? 'translateY(-6px)' : 'none',
                cursor: 'default',
              }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>{f.icon}</div>
              <h3 style={{ color: f.color, fontSize: 18, fontWeight: 900, marginBottom: 2 }}>{f.title}</h3>
              <div style={{ color: '#8b90a8', fontSize: 12, fontWeight: 600, marginBottom: 12 }}>{f.subtitle}</div>
              <p style={{ color: '#8b90a8', fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>{f.desc}</p>
              <div style={{ fontSize: 28, fontWeight: 900, color: f.color, marginBottom: 4 }}>{f.price} <span style={{ fontSize: 14, color: '#6272a4' }}>RON/lecție</span></div>
              <div style={{ fontSize: 11, color: '#6272a4', marginBottom: 14 }}>✓ {f.ideal}</div>
              <button onClick={openEnrollGeneral} style={{
                background: `${f.color}20`, border: `1px solid ${f.color}40`,
                borderRadius: 10, padding: '8px 20px', color: f.color,
                fontWeight: 700, fontSize: 12, cursor: 'pointer',
                fontFamily: "'Nunito', sans-serif", transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.target.style.background = f.color; e.target.style.color = '#0d0e12'; }}
              onMouseLeave={e => { e.target.style.background = `${f.color}20`; e.target.style.color = f.color; }}>
                Înscrie-te →
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* COURSES */}
      <section style={{ padding: '40px 24px 80px', position: 'relative', zIndex: 10, maxWidth: 900, margin: '0 auto' }}>
        <h2 style={{ textAlign: 'center', fontSize: 'clamp(22px, 4vw, 32px)', marginBottom: 40, fontWeight: 900 }}>
          <span style={{ color: '#f1fa8c' }}>✨</span> Alege-ți super-puterea <span style={{ color: '#f1fa8c' }}>✨</span>
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
          {courses.map((c, i) => (
            <div key={i}
              onClick={() => setSelectedCourse(c)}
              style={{
                background: bgColors[c.color] || 'rgba(189,147,249,0.12)',
                border: `2px solid ${borderColors[c.color] || 'rgba(189,147,249,0.3)'}`,
                borderRadius: 20, padding: 24, textAlign: 'center',
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                cursor: 'pointer',
                animation: 'fadeInUp 0.6s ease forwards',
                animationDelay: `${i * 0.15}s`, opacity: 0,
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-8px) scale(1.03)';
                e.currentTarget.style.boxShadow = `0 12px 40px ${borderColors[c.color]}`;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
              <div style={{ fontSize: 48, marginBottom: 8, animation: 'wobble 3s ease-in-out infinite', animationDelay: `${i * 0.5}s` }}>{c.emoji}</div>
              <h3 style={{ color: c.color, fontSize: 20, fontWeight: 900, marginBottom: 4 }}>{c.title}</h3>
              <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.1)', borderRadius: 20, padding: '2px 12px', fontSize: 11, color: '#f1fa8c', fontWeight: 700, marginBottom: 10 }}>{c.age_range}</div>
              <p style={{ color: '#b8c0d4', fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>{c.short_description}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6272a4', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 10 }}>
                <span>📚 {c.total_lessons} lecții</span>
                <span>🎯 {c.level_display}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Lecție demo gratuită */}
        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <button onClick={() => { setIsDemo(true); setEnrollCourse({ emoji: '🎁', title: 'Orice curs', age_range: '7-11 ani' }); }} style={{
            background: 'linear-gradient(135deg, #50fa7b, #8be9fd)',
            border: 'none', borderRadius: 50, padding: '16px 40px',
            color: '#0d0e12', fontFamily: "'Nunito', sans-serif",
            fontWeight: 800, fontSize: 16, cursor: 'pointer',
            boxShadow: '0 4px 25px rgba(80,250,123,0.3)',
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => e.target.style.transform = 'scale(1.05)'}
          onMouseLeave={e => e.target.style.transform = 'scale(1)'}>
            🎁 Prima lecție e GRATUITĂ — Programeaz-o acum!
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ position: 'relative', zIndex: 10 }}>
        <CastleSilhouette color="rgba(10,10,25,0.8)" />
        <div style={{ background: 'rgba(10,10,25,0.8)', padding: '24px 0 16px', textAlign: 'center' }}>
          <span style={{ color: '#6272a4', fontSize: 12 }}>
            © 2026 DraculaDev Kids Academy — Aventura codului începe aici! 🦇
          </span>
        </div>
      </footer>

      {/* MODALS */}

      {showSlideshow && (
  <CourseSlideshow onClose={() => setShowSlideshow(false)} />
)}
      {selectedCourse && (
        <CourseDetail
          course={selectedCourse}
          onClose={() => setSelectedCourse(null)}
          onEnroll={handleEnroll}
          
        />
      )}
      {enrollCourse && (
        <EnrollmentForm
          course={enrollCourse}
          isDemo={isDemo}
          onClose={() => { setEnrollCourse(null); setIsDemo(false); }}
        />
      )}
    </div>
  );
};

export default KidsPage;